import React, { useEffect, useState, useCallback } from 'react';
import { DposInterface__factory } from '../../typechain-types/factories/dpos_contract_interface.sol/DposInterface__factory';
import { useWallet } from '../../context/WalletContext';
import KPICard from '../kpi/KPICard';
import KPICardsContainer from '../kpi/KPICardsContainer';
import ValidatorService from '../../services/ValidatorService';
import DelegationService, { DelegationSummary } from '../../services/DelegationService';
import { ethers } from 'ethers';
import { TARGET_VALIDATOR, DPOS_CONTRACT_ADDRESS, RPC_URL } from '../../config/networkConfig';
import taraxaLogo from '../../assets/taraxa-logo.png';
import DelegateModal from '../modals/DelegateModal';
import RedelegateModal from '../modals/RedelegateModal';

// Create a component for validator KPIs that uses the WalletContext
const ValidatorKPIs: React.FC = () => {
  const { account, isConnected } = useWallet();
  const [totalStake, setTotalStake] = useState<string>('0');
  const [commissionReward, setCommissionReward] = useState<string>('0');
  const [commission, setCommission] = useState<string>('0');
  const [validatorAddress] = useState<string>(TARGET_VALIDATOR);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState<boolean>(false);
  const [walletBalance, setWalletBalance] = useState<string>('0');
  const [isRedelegateModalOpen, setIsRedelegateModalOpen] = useState<boolean>(false);
  
  // New state to check if the connected wallet is the validator
  const [isValidator, setIsValidator] = useState<boolean>(false);

  // States for delegation information
  const [delegations, setDelegations] = useState<DelegationSummary>({
    targetValidatorAmount: BigInt(0),
    otherValidatorsAmount: BigInt(0),
    targetValidatorRewards: BigInt(0),
    otherValidators: [],
    isLoading: false
  });
  
  const [isLoadingValidatorInfo, setIsLoadingValidatorInfo] = useState(false);
  const [isClaimingCommission, setIsClaimingCommission] = useState(false);

  // Check if connected account is the validator
  useEffect(() => {
    if (isConnected && account) {
      const isCurrentValidator = account.toLowerCase() === TARGET_VALIDATOR.toLowerCase();
      setIsValidator(isCurrentValidator);
      console.log("Is validator check:", account, TARGET_VALIDATOR, isCurrentValidator);
    } else {
      setIsValidator(false);
    }
  }, [account, isConnected]);

  // Initialize validator service when component mounts
  useEffect(() => {
    const initService = async () => {
      // Create a provider that doesn't rely on window.ethereum
      // This ensures we can fetch validator data even when wallet is not connected
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      ValidatorService.initContract(provider);
      DelegationService.initContract(provider);
      
      await refreshValidatorInfo(validatorAddress, provider);
      
      // If wallet is available, also set up with wallet provider
      if (window.ethereum) {
        const walletProvider = new ethers.BrowserProvider(window.ethereum);
        ValidatorService.initContract(walletProvider);
        DelegationService.initContract(walletProvider);
      }
    };
    
    initService();
  }, [validatorAddress]);

  // All refresh functions wrapped with useCallback to prevent dependency warnings
  const refreshTotalStake = useCallback(async (): Promise<string> => {
    if (!validatorAddress) return totalStake;
    
    try {
      // Use a direct provider that doesn't depend on wallet connection
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      ValidatorService.initContract(provider);
      
      const info = await ValidatorService.getValidatorInfo(validatorAddress);
      const formatted = ethers.formatEther(info.totalStake);
      setTotalStake(formatted);
      return formatted;
    } catch (error) {
      console.error('Error refreshing total stake:', error);
      return totalStake;
    }
  }, [totalStake, validatorAddress]);

  const refreshCommission = useCallback(async (): Promise<string> => {
    if (!validatorAddress) return commission;
    
    try {
      // Use a direct provider that doesn't depend on wallet connection
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      ValidatorService.initContract(provider);
      
      const info = await ValidatorService.getValidatorInfo(validatorAddress);
      // Format commission as percentage
      const commissionBasisPoints = Number(info.commission);
      const commissionPercentage = (commissionBasisPoints / 100).toFixed(0);
      setCommission(commissionPercentage);
      return commissionPercentage;
    } catch (error) {
      console.error('Error refreshing commission:', error);
      return commission;
    }
  }, [commission, validatorAddress]);

  const refreshCommissionReward = useCallback(async (): Promise<string> => {
    if (!validatorAddress || !isValidator) return commissionReward;
    
    try {
      // Use a direct provider that doesn't depend on wallet connection
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      ValidatorService.initContract(provider);
      
      const info = await ValidatorService.getValidatorInfo(validatorAddress);
      const formatted = ethers.formatEther(info.commissionReward);
      setCommissionReward(formatted);
      return formatted;
    } catch (error) {
      console.error('Error refreshing commission reward:', error);
      return commissionReward;
    }
  }, [commissionReward, isValidator, validatorAddress]);

  const refreshDelegations = useCallback(async (): Promise<string> => {
    if (!isConnected || !account) return '0';
    
    try {
      setDelegations(prev => ({ ...prev, isLoading: true }));
      const summary = await DelegationService.getDelegationSummary(account);
      setDelegations(summary);
      return ethers.formatEther(summary.targetValidatorAmount);
    } catch (error) {
      console.error('Error refreshing delegations:', error);
      return '0';
    }
  }, [account, isConnected]);

  const refreshOtherDelegations = useCallback(async (): Promise<string> => {
    if (!isConnected || !account) return '0';
    
    try {
      if (delegations.isLoading) {
        // If already loading, don't trigger another load
        return ethers.formatEther(delegations.otherValidatorsAmount);
      }
      
      const summary = await DelegationService.getDelegationSummary(account);
      setDelegations(summary);
      return ethers.formatEther(summary.otherValidatorsAmount);
    } catch (error) {
      console.error('Error refreshing other delegations:', error);
      return '0';
    }
  }, [account, delegations.isLoading, delegations.otherValidatorsAmount, isConnected]);

  const refreshRewards = useCallback(async (): Promise<string> => {
    if (!isConnected || !account) return '0';
    
    try {
      if (delegations.isLoading) {
        // If already loading, don't trigger another load
        return ethers.formatEther(delegations.targetValidatorRewards);
      }
      
      const summary = await DelegationService.getDelegationSummary(account);
      setDelegations(summary);
      return ethers.formatEther(summary.targetValidatorRewards);
    } catch (error) {
      console.error('Error refreshing rewards:', error);
      return '0';
    }
  }, [account, delegations.isLoading, delegations.targetValidatorRewards, isConnected]);

  // Refresh wallet balance
  const refreshWalletBalance = useCallback(async (): Promise<string> => {
    if (!isConnected || !account || !window.ethereum) return walletBalance;
    
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const balance = await provider.getBalance(account);
      const formatted = ethers.formatEther(balance);
      setWalletBalance(formatted);
      return formatted;
    } catch (error) {
      console.error('Error refreshing wallet balance:', error);
      return walletBalance;
    }
  }, [account, isConnected, walletBalance]);

  const refreshValidatorInfo = async (address: string, customProvider?: ethers.Provider) => {
    setIsLoadingValidatorInfo(true);
    try {
      if (!address) return;
      
      // Use the provided custom provider, or create a fallback provider
      // This ensures validator data can be fetched even without wallet connection
      const provider = customProvider || new ethers.JsonRpcProvider(RPC_URL);
      ValidatorService.initContract(provider);
      
      const info = await ValidatorService.getValidatorInfo(address);
      // Use ethers.formatEther directly
      setTotalStake(ethers.formatEther(info.totalStake));
      setCommissionReward(ethers.formatEther(info.commissionReward));
      
      // Format commission as percentage (assuming it's stored as basis points, e.g. 1000 = 10%)
      const commissionBasisPoints = Number(info.commission);
      const commissionPercentage = (commissionBasisPoints / 100).toFixed(2);
      setCommission(commissionPercentage);
    } catch (error) {
      console.error('Error refreshing validator info:', error);
    } finally {
      setIsLoadingValidatorInfo(false);
    }
  };

  // Combine all refresh functions into one
  const refreshAll = useCallback(() => {
    refreshTotalStake();
    refreshCommission();
    refreshDelegations();
    refreshOtherDelegations();
    refreshRewards();
    refreshWalletBalance();
    
    // Only refresh commission reward if connected wallet is the validator
    if (isValidator) {
      refreshCommissionReward();
    }
  }, [
    refreshTotalStake, refreshCommission, refreshDelegations, 
    refreshOtherDelegations, refreshRewards, refreshWalletBalance,
    refreshCommissionReward, isValidator
  ]);

  // Set up polling for data refresh
  useEffect(() => {
    if (isConnected) {
      // Initial refresh
      refreshAll();
      
      // Set up polling
      const REFRESH_INTERVAL = 30000; // 30 seconds
      const interval = setInterval(refreshAll, REFRESH_INTERVAL);
      
      return () => clearInterval(interval);
    }
  }, [isConnected, refreshAll]);

  // Handler for delegate button
  const handleDelegate = async () => {
    if (!isConnected || isProcessingAction) return;
    
    try {
      // Refresh the wallet balance before opening the modal to ensure it's current
      await refreshWalletBalance();
      setIsDelegateModalOpen(true);
    } catch (error) {
      console.error('Error initiating delegation:', error);
    }
  };

  // Handler for claim commission rewards
  const handleClaimCommissionRewards = async () => {
    if (!isConnected || isProcessingAction || !isValidator) return;
    
    try {
      setIsClaimingCommission(true);
      setIsProcessingAction(true);
      
      if (!window.ethereum) {
        throw new Error('No Ethereum provider found');
      }
      
      // Get the provider and signer
      const provider = new ethers.BrowserProvider(window.ethereum as ethers.Eip1193Provider);
      const signer = await provider.getSigner();
      
      // Initialize the contract with signer
      const contract = DposInterface__factory.connect(DPOS_CONTRACT_ADDRESS, signer);
      
      // Call the contract method to claim commission rewards
      console.log('Claiming commission rewards for validator:', validatorAddress);
      const tx = await contract.claimCommissionRewards(validatorAddress);
      
      // Wait for transaction to be confirmed
      await tx.wait();
      console.log('Commission rewards claimed successfully');
      
      // Refresh the validator info to update commission rewards
      await refreshCommissionReward();
      
      // Also refresh wallet balance since rewards are sent to wallet
      await refreshWalletBalance();
      
      // Show success notification
      alert('Commission rewards claimed successfully!');
      
    } catch (error) {
      console.error('Error claiming commission rewards:', error);
      alert('Failed to claim commission rewards. See console for details.');
    } finally {
      setIsClaimingCommission(false);
      setIsProcessingAction(false);
    }
  };

  const closeDelegateModal = () => {
    setIsDelegateModalOpen(false);
  };

  const closeRedelegateModal = () => {
    setIsRedelegateModalOpen(false);
  };

  // Handler for redelegate button
  const handleRedelegate = async () => {
    if (!isConnected || isProcessingAction) return;
    
    try {
      // Open the redelegate modal
      setIsRedelegateModalOpen(true);
    } catch (error) {
      console.error('Error initiating redelegation:', error);
    }
  };

  // Handler for claiming rewards
  const handleClaimRewards = async () => {
    if (!isConnected || isProcessingAction || !account) return;
    
    try {
      setIsProcessingAction(true);
      
      if (!window.ethereum) {
        throw new Error('No Ethereum provider found');
      }
      
      // Get the provider and signer
      const provider = new ethers.BrowserProvider(window.ethereum as ethers.Eip1193Provider);
      const signer = await provider.getSigner();
      
      // Initialize the contract with signer
      DelegationService.initContract(provider);
      const contract = DposInterface__factory.connect(DPOS_CONTRACT_ADDRESS, signer);
      
      // Call the contract method to claim rewards
      console.log('Claiming rewards from validator:', validatorAddress);
      const tx = await contract.claimRewards(validatorAddress);
      
      // Wait for transaction to be confirmed
      await tx.wait();
      console.log('Rewards claimed successfully');
      
      // Refresh the delegation data to update rewards
      const summary = await DelegationService.getDelegationSummary(account);
      setDelegations(summary);
      
      // Also refresh wallet balance since rewards are sent to wallet
      await refreshWalletBalance();
      
      // Show success notification
      alert('Rewards claimed successfully!');
      
    } catch (error) {
      console.error('Error claiming rewards:', error);
      alert('Failed to claim rewards. See console for details.');
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Create a Taraxa logo image element
  const TaraxaLogoIcon = () => (
    <img 
      src={taraxaLogo} 
      alt="TARA" 
      style={{ 
        width: '20px', 
        height: '20px', 
        objectFit: 'contain',
        verticalAlign: 'middle'
      }} 
    />
  );

  // Percentage icon using SVG
  const PercentageIcon = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ verticalAlign: 'middle' }}
    >
      <path 
        d="M19 5L5 19" 
        stroke="#9b59b6" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      <circle 
        cx="6.5" 
        cy="6.5" 
        r="3.5" 
        stroke="#9b59b6" 
        strokeWidth="2" 
        fill="none"
      />
      <circle 
        cx="17.5" 
        cy="17.5" 
        r="3.5" 
        stroke="#9b59b6" 
        strokeWidth="2" 
        fill="none"
      />
    </svg>
  );

  // Commission rewards icon using SVG
  const CommissionIcon = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ verticalAlign: 'middle' }}
    >
      <circle 
        cx="12" 
        cy="12" 
        r="9" 
        stroke="#e74c3c" 
        strokeWidth="2" 
        fill="none"
      />
      <path 
        d="M14.5 9.5L9.5 14.5M9.5 9.5L14.5 14.5" 
        stroke="#e74c3c" 
        strokeWidth="2" 
        strokeLinecap="round" 
      />
      <path 
        d="M12 6V8M12 16V18M6 12H8M16 12H18" 
        stroke="#e74c3c" 
        strokeWidth="2" 
        strokeLinecap="round" 
      />
    </svg>
  );

  // Rewards icon using SVG
  const RewardsIcon = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ verticalAlign: 'middle' }}
    >
      <path 
        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
        stroke="#e67e22" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );

  return (
    <>
    <KPICardsContainer>
      <KPICard 
        title="Total TARA staked" 
        value={totalStake}
        color="green"
        formatValue={true}
        onRefresh={refreshTotalStake}
        icon={<TaraxaLogoIcon />}
        loading={isLoadingValidatorInfo}
      />
      <KPICard 
        title="Current commission rate" 
        value={`${commission}`}
        color="purple"
        onRefresh={refreshCommission}
        icon={<PercentageIcon />}
        loading={isLoadingValidatorInfo}
      />
      
      {/* Commission Reward KPI - Only shown when wallet is validator */}
      {isValidator && (
        <KPICard 
          title="Your commission rewards" 
          value={commissionReward}
          color="green"
          formatValue={true}
          onRefresh={refreshCommissionReward}
          icon={<CommissionIcon />}
          loading={isLoadingValidatorInfo}
          buttonText="Claim"
          onButtonClick={handleClaimCommissionRewards}
          buttonDisabled={isClaimingCommission || parseFloat(commissionReward) <= 0}
        />
      )}
      
      {/* Always show these KPIs but disable them when wallet is not connected */}
      <KPICard 
        title="Your stake with us" 
        value={isConnected ? ethers.formatEther(delegations.targetValidatorAmount) : "—"}
        color="blue"
        formatValue={true}
        onRefresh={isConnected ? refreshDelegations : undefined}
        icon={<TaraxaLogoIcon />}
        loading={delegations.isLoading}
        buttonText="Delegate"
        onButtonClick={isConnected ? handleDelegate : undefined}
        buttonDisabled={!isConnected || isProcessingAction || delegations.isLoading}
        disabled={!isConnected}
        disabledMessage="Connect wallet to view"
      />
      <KPICard 
        title="Your stake elsewhere" 
        value={isConnected ? ethers.formatEther(delegations.otherValidatorsAmount) : "—"}
        color="green"
        formatValue={true}
        onRefresh={isConnected ? refreshOtherDelegations : undefined}
        icon={<TaraxaLogoIcon />}
        loading={delegations.isLoading}
        buttonText="Redelegate"
        onButtonClick={isConnected ? handleRedelegate : undefined}
        buttonDisabled={!isConnected || isProcessingAction || delegations.isLoading || delegations.otherValidatorsAmount <= 0}
        disabled={!isConnected}
        disabledMessage="Connect wallet to view"
      />
      <KPICard 
        title="Your rewards" 
        value={isConnected ? ethers.formatEther(delegations.targetValidatorRewards) : "—"}
        color="blue"
        formatValue={true}
        onRefresh={isConnected ? refreshRewards : undefined}
        icon={<RewardsIcon />}
        loading={delegations.isLoading}
        buttonText="Claim"
        onButtonClick={isConnected ? handleClaimRewards : undefined}
        buttonDisabled={!isConnected || isProcessingAction || delegations.isLoading || delegations.targetValidatorRewards <= 0}
        disabled={!isConnected}
        disabledMessage="Connect wallet to view"
      />
    </KPICardsContainer>
    <DelegateModal
        isOpen={isDelegateModalOpen}
        onClose={closeDelegateModal}
        availableBalance={walletBalance}
        validatorName="Icelands 1st validator"
    />
    <RedelegateModal
      isOpen={isRedelegateModalOpen}
      onClose={closeRedelegateModal}
      otherValidators={delegations.otherValidators}
      targetValidatorName="Icelands 1st validator"
    />
    </>
  );
};

export default ValidatorKPIs;